/// Domain model shared between client and server.
namespace Shared.Domain
   
open System

type Validated =
    | Success
    | Failure of string list
    with
        static member ToResult =
            function
            | Success -> Ok ()
            | Failure reasons -> Error reasons

type DrugName =
    | Alcohol = 1
    | Amphetamines = 2
    | Cocaine = 3
    | Ecstasy = 4
    | Ketamine = 5
    | Cannabis = 6
    | CrackCocaine = 7
    | PrescribedOrOverTheCounter = 8
    | Spice = 9
    | OtherDrug = 10
    | Opiates = 11

type Behaviour =
    | Default = 1
    | Audit = 2
    | NoRefer = 3

[<CLIMutable>]
type DrugConfig =
    {
        Name: DrugName
        Behaviour: Behaviour
    }

type APIVersion =
    | V2024 = 1
    | Current = 2

[<CLIMutable>]
type ReferralInfo =
    {
        DrugPathways: DrugConfig array
        AllowedPostcodes: string array
        ContactNumber: string
        APIRoot: string
        APIVersion: APIVersion
        PolicyUrl: string
    }

[<CLIMutable>]
type TenantDisplay =
    {
        Name: string
        Area: string
        Location: string
        ContactNumber: string
        LandingPage: string option
    }

type ReferralHelpPack =
    { 
        Name: string; 
        Link: string
    }

type CategoryRef = CategoryRef of int

type TenantRef = TenantRef of string
    with static member get = function TenantRef t -> t
type ServiceRef = ServiceRef of int
type ImageRef = ImageRef of int

type Slug = Slug of string
    with 
        static member get = function Slug str -> str 
        static member map f = function Slug str -> f str |> Slug

type ScopedTarget = { Tenant: TenantRef; Slug: Slug }

type User =
    {
        Id: int;
        Username: string
        Tenant: TenantRef
        Email: string
        EmailHash: string
    }

type PasswordResetRequest =
    {
        UserId: int
        Date: DateTime
        Id: Guid
    }

type ReplacementPassword =
    {
        Id: System.Guid
        Password: string
    }

type EmailEntry = Email of string
    with
        static member Get = function Email e -> e

type UserEmail =
    {
        UserId: int
        Email: string
    }

type JWT =
    {
        Account: User
        Token: string
    }

type LoginResponse =
    | SignIn of JWT
    | LoginFailure of string
    | SetMFA of string * string

type Login = 
    { 
        UserName : string
        Password : string
        PasswordId : Guid
        ResetEmail: string
        Code: string
    }

type Service =
    {
        Id: int;
        Name: string;
        Tenant: TenantRef;
        Categories: CategoryRef list;
    }

type Font =
    | Pacifico
    | ABeeZee
    | Arial
    | Helvetica
    | TimesNewRoman

type IThemeItem =
    | Background of string
    | Colour of string
    | Font of Font
    | FontSize of string

type ColourAccent =
    | Inherited
    | Light
    | Dark
    | Turquoise
    | DarkRed
    | Blue
    | Grey

type Theme = IThemeItem list

type BlockContent =
    | RawText of string
    | Markdown of string
    | PageNavigation
    | YouTubeEmbed of link: string * height: string * width: string * allowFullScreen: bool

type Justification =
    | LeftAligned
    | Centered

type Block =
    {
        Id: Guid;
        Header: (ColourAccent*BlockContent) option;
        Content: BlockContent;
        Theme: Theme
        Justification: Justification
    }
    with static member GetId b = b.Id

module BlockModule =
    let map (f: BlockContent -> BlockContent) b = 
        { b with Header = Option.map (fun (a,c) -> a, f c) b.Header; Content = f b.Content }

type ImageShape =
    | Rectangle
    | RoundedRectangle
    | Circle
    | Thumbnail

type ImageBlock =
    {
        Url: string;
        HoverText: string
        Alt: string;
    }

type TextBlock =
    | SingleColumn of id: Guid * Block list
    | TwoColumn of id: Guid * left:Block list * right:Block list
    | MainAndSideBar of id: Guid * left:Block list * right:Block list
    | CardList of id: Guid * ImageShape * ((ImageBlock * Block) list)
    with
        static member MapList (f: Block list -> Block list) (tb: TextBlock) =
            match tb with
            | SingleColumn (id, blocks) -> SingleColumn (id, (f blocks))
            | TwoColumn (id,l,r) -> TwoColumn (id,f l,f r)
            | MainAndSideBar (id,main,sb) -> MainAndSideBar (id, f main, f sb)
            | CardList (id, shape, items) -> CardList (id, shape, items |> List.map (fun (img,blocks) -> img, f [blocks] |> List.head))
        static member Map (f: Block -> Block) (tb: TextBlock) = TextBlock.MapList (List.map f) tb
            
        static member Id =
            function
            | SingleColumn (id,_) -> id
            | TwoColumn (id,_,_) -> id
            | MainAndSideBar (id,_,_) -> id
            | CardList (id,_,_) -> id

type PageWidth =
    | WholePage
    | Centered

type Location =
    {
        Id: Slug
        Name: string
    }
        with
            static member getId l = Slug.get l.Id
             static member mapId f (l:Location) = { l with Id = f l.Id }

type PageMeta =
    {
        Location: Location
        IsLandingPage: bool
        Training: bool
    }

type TreeNodeDTO =
    | Node of ref:Location * children: TreeNodeDTO []
    with
        member this.Children = this |> function Node (_, c) -> c
        member this.Key = this |> function Node (ref,_) -> ref

type  Hierarchy =
    {
        Breadcrumbs: Location list
        Siblings: Location list
    }
    with
        static member none = { Breadcrumbs = []; Siblings = [] }

type GenericPage =
    {
        Location: Location
        Tenant: TenantRef
        Header: Block option
        BannerImage: ImageRef option
        TextBlocks: TextBlock list
        Theme: Theme
        PageWidth: PageWidth 
        IsLandingPage: bool
    }

type Snapshot<'a> =
    {
        Content: 'a;
        IdentifierWhenLoaded: Slug
    }

type PageResult =
    {
        Page: GenericPage
        Hierarchy: Hierarchy
    }

type BlockTranslationContext =
    {
        Block: TextBlock;
        Page: GenericPage;
        Hierarchy: Hierarchy
    }

type Page =
    | Generic of GenericPage

type ServiceContent =
    {
        Service: Service
        Pages: Page list
    }

type Direction =
    | Up
    | Down

type FileMeta =
    {
        Name: string;
        Size: int;
        DateUploaded: DateTime;
        Thumbnail: string;
        Image: bool
    }

type Gender =
    | Male
    | Female
    | NotKnown
    | NotSpecified

module Validation =
    let combine r1 r2 =
        match r1,r2 with
        | Success, Success -> Success
        | Failure f, Success | Success, Failure f -> Failure f
        | Failure f1, Failure f2 -> Failure (f1@f2)

    let bind f1 f2 =
        fun x -> combine (f1 x) (f2 x)

    let (<*>) = bind

    let (<?>) (b: bool) (s: string) =
        if b then Failure [s] else Success

module HelpPacks =
    type PackDownloads =
        {
            Alcohol: string*string;
            Amphetamines: string*string;
            Cocaine: string*string;
            Ecstasy: string*string;
            Ketamine: string*string;
            Cannabis: string*string;
            Opiates: string*string;
            Crack: string*string;
            PrescribedOrOverTheCounter: string*string;
            Spice: string*string;
            OtherDrug: string*string;
        }

module SharedValidations =
    open Validation
    open System.Text.RegularExpressions

    let ``email address must contain at`` (email: string) =
        not (email.Contains "@") <?> "The email must contain the '@' character."

    let ``email address must contain dot`` (email: string) =
        not (email.Contains ".") <?> "The email must contain the '.' character."

    let ``email address must not contain space`` (email: string) =
        email.Contains " " <?> "The email must not contain a space."

    let emailValidations =
        ``email address must contain at``
        <*> ``email address must contain dot``
        <*> ``email address must not contain space``

    let ``postcode must contain space`` (postcode: string) =
        not (postcode.Contains " ") <?> "The postcode must contain a space."

    let ``postcode must match regex`` (postcode: string) =
        let regex = Regex(@"^(zz99 3vz|gir 0aa|[a-pr-uwyz]([0-9]{1,2}|([a-hik-y][0-9]|[a-hk-y][0-9]([0-9]|[abehmnprv-y]))|[0-9][a-hjkps-uw]) [0-9][abd-hjlnp-uw-z]{2})$", RegexOptions.IgnoreCase)
        not (regex.IsMatch postcode) <?> "The postcode must be valid."

    let postcodeValidations =
        ``postcode must contain space``
        <*> ``postcode must match regex``

module AlcoholAuditDomain =
    // Alcohol Audit

    type DrinkFrequency =
    | Never
    | Monthly
    | MultiplePerMonth
    | FewTimesAWeek
    | MostDays

    type Frequency =
    | Never
    | LessThanMonthly
    | Monthly
    | Weekly
    | AlmostDaily

    type Occurance =
    | No
    | NotInLastYear
    | InLastYear    

    [<CLIMutable>] // used in config
    type AlcoholWording =
        {
            LowRiskText: string[]
            ConditionText: string[]
            DependenceText: string[]
            ReferOnLow: bool
            ReferOnIncreasing: bool
            ReferOnHigh: bool
            ReferOnDependence: bool
        }


    type AlcoholAuditAnswers = 
        { 
            Gender: Gender
            DrinkFrequency: DrinkFrequency
            HowManyInDay: int
            BingeFrequency: Frequency
            UnableToStop: Frequency
            FailedToDo: Frequency
            NeededADrink: Frequency
            Remorse: Frequency
            UnableToRemember: Frequency
            Injured: Occurance
            FriendConcerned: Occurance
        }

    let drinkFrequencyScore =
        function
        | DrinkFrequency.Never -> 0
        | DrinkFrequency.Monthly -> 1
        | MultiplePerMonth -> 2
        | FewTimesAWeek -> 3
        | MostDays -> 4

    let frequencyScore =
        function
        | Frequency.Never -> 0
        | LessThanMonthly -> 1
        | Monthly -> 2
        | Weekly -> 3
        | AlmostDaily -> 4

    let occuranceScore =
        function
        | No -> 0
        | NotInLastYear -> 2
        | InLastYear -> 4
 
    let howManyScore =
        function 
        | -1 -> 0
        | 0 | 1 | 2 -> 0
        | 3 | 4 -> 1
        | 5 | 6 -> 2
        | 7 | 8 | 9 -> 3
        | _ -> 4

    let calculateAuditScore answers =
        drinkFrequencyScore answers.DrinkFrequency
        + howManyScore answers.HowManyInDay
        + frequencyScore answers.BingeFrequency
        + frequencyScore answers.UnableToStop
        + frequencyScore answers.FailedToDo
        + frequencyScore answers.NeededADrink
        + frequencyScore answers.Remorse
        + frequencyScore answers.UnableToRemember
        + occuranceScore answers.Injured
        + occuranceScore answers.FriendConcerned
open AlcoholAuditDomain

module ReferralForms =
    let sexualities =
        [
            "", "Not entered"
            "9", "Not answered"; 
            "Z", "I don't want to say"; 
            "U", "I'm not sure"; 
            "1", "Hetrosexual or straight"; 
            "2", "Gay or lesbian"; 
            "3", "Bisexual"; 
            "4", "Something else not listed here"
        ]

    let ethnicities =
        [
            "", "Not entered"
            "Z", "Not answered"; 
            "99", "I don't want to say or I don't know"; 
            "A", "White British"; 
            "B", "White Irish"; 
            "C", "Other White"; 
            "D", "White and Black Caribbean"
            "E", "White and Black African"
            "F", "White and Asian"
            "G", "Other mixed"
            "H", "Indian"
            "J", "Pakistani"
            "K", "Bangladeshi"
            "L", "Other Asian"
            "M", "Carribean"
            "N", "African"
            "P", "Other Black"
            "R", "Chinese"
            "S", "Other"
        ]

    let nationalities =
        [
            "", "Not entered"
            "GBR", "United Kingdom"
            "AFG", "Afghanistan"
            "ALB", "Albania"
            "DZA", "Algeria"
            "ASM", "American Samoa"
            "AND", "Andorra"
            "AGO", "Angola"
            "ATG", "Antigua and Barbuda"
            "ARG", "Argentina"
            "ARM", "Armenia"
            "AUS", "Australia"
            "AUT", "Austria"
            "AZE", "Azerbaijan"
            "BHS", "Bahamas"
            "BHR", "Bahrain"
            "BGD", "Bangladesh"
            "BRB", "Barbados"
            "BLR", "Belarus"
            "BEL", "Belgium"
            "BLZ", "Belize"
            "BEN", "Benin"
            "BTN", "Bhutan"
            "BES", "Bonaire, Sint Eustatius and Saba"
            "BIH", "Bosnia and Herzegovina"
            "BWA", "Botswana"
            "BRA", "Brazil"
            "IOT", "British Indian Ocean Territory"
            "BRN", "Brunei Darussalam"
            "BGR", "Bulgaria"
            "BFA", "Burkina Faso"
            "BDI", "Burundi"
            "CPV", "Cabo Verde"
            "KHM", "Cambodia"
            "CMR", "Cameroon"
            "CAN", "Canada"
            "CAF", "Central African Republic"
            "CHL", "Chile"
            "CHN", "China"
            "COL", "Colombia"
            "COM", "Comoros"
            "COG", "Congo"
            "COD", "Congo (the Democratic Republic of the)"
            "CRI", "Costa Rica"
            "HRV", "Croatia"
            "CUB", "Cuba"
            "CUW", "Curaçao"
            "CYP", "Cyprus"
            "CZE", "Czech Republic"
            "DNK", "Denmark"
            "DJI", "Djibouti"
            "DMA", "Dominica"
            "DOM", "Dominican Republic"
            "ECU", "Ecuador"
            "EGY", "Egypt"
            "SLV", "El Salvador"
            "ENG", "English"
            "GNQ", "Equatorial Guinea"
            "ERI", "Eritrea"
            "EST", "Estonia"
            "ETH", "Ethiopia"
            "FJI", "Fiji"
            "FIN", "Finland"
            "FRA", "France"
            "GUF", "French Guiana"
            "GAB", "Gabon"
            "GMB", "Gambia"
            "GEO", "Georgia"
            "DEU", "Germany"
            "GHA", "Ghana"
            "GRC", "Greece"
            "GRD", "Grenada"
            "GUM", "Guam"
            "GTM", "Guatemala"
            "GIN", "Guinea"
            "GNB", "Guinea-Bissau"
            "GUY", "Guyana"
            "HTI", "Haiti"
            "HND", "Honduras"
            "HKG", "Hong Kong"
            "HUN", "Hungary"
            "ISL", "Iceland"
            "IND", "India"
            "IDN", "Indonesia"
            "IRAN", "Iran"
            "IRN", "Iran Islamic Republic of"
            "IRQ", "Iraq"
            "IRL", "Ireland"
            "ISR", "Israel"
            "ITA", "Italy"
            "JAM", "Jamaica"
            "JPN", "Japan"
            "JOR", "Jordan"
            "KAZ", "Kazakhstan"
            "KEN", "Kenya"
            "KIR", "Kiribati"
            "KOR", "Korea"
            "KWT", "Kuwait"
            "LAO", "Lao People's Democratic Republic"
            "LVA", "Latvia"
            "LBN", "Lebanon"
            "LBY", "Libya"
            "LTU", "Lithuania"
            "LUX", "Luxembourg"
            "MKD", "Macedonia the former Yugoslav Republic of"
            "MWI", "Malawi"
            "MYS", "Malaysia"
            "MDV", "Maldives"
            "MLI", "Mali"
            "MLT", "Malta"
            "MTQ", "Martinique"
            "MRT", "Mauritania"
            "MUS", "Mauritius"
            "MEX", "Mexico"
            "FSM", "Micronesia Federated States of"
            "MDA", "Moldova Republic of"
            "MNG", "Mongolia"
            "MNE", "Montenegro"
            "MAR", "Morocco"
            "MOZ", "Mozambique"
            "NAM", "Namibia"
            "NRU", "Nauru"
            "NPL", "Nepal"
            "NLD", "Netherlands"
            "NZL", "New Zealand"
            "NIC", "Nicaragua"
            "NGA", "Nigeria"
            "NOR", "Norway"
            "Z", "Not Stated"
            "OMN", "Oman"
            "PAK", "Pakistan"
            "PLW", "Palau"
            "PSE", "Palestinian Territory Occupied"
            "PAN", "Panama"
            "PNG", "Papua New Guinea"
            "PRY", "Paraguay"
            "PER", "Peru"
            "PHL", "Philippines"
            "POL", "Poland"
            "PRT", "Portugal"
            "PRI", "Puerto Rico"
            "QAT", "Qatar"
            "ROU", "Romania"
            "RUS", "Russian Federation"
            "RWA", "Rwanda"
            "BLM", "Saint Barthélemy"
            "MAF", "Saint Martin (French part)"
            "SAU", "Saudi Arabia"
            "SEN", "Senegal"
            "SRB", "Serbia"
            "SLE", "Sierra Leone"
            "SGP", "Singapore"
            "SVK", "Slovakia"
            "SVN", "Slovenia"
            "SOM", "Somalia"
            "ZAF", "South Africa"
            "SGS", "South Georgia and the South Sandwich Islands"
            "SSD", "South Sudan"
            "ESP", "Spain"
            "LKA", "Sri Lanka"
            "SDN", "Sudan"
            "SWZ", "Swaziland"
            "SWE", "Sweden"
            "CHE", "Switzerland"
            "SYR", "Syrian Arab Republic"
            "TWN", "Taiwan Province China of"
            "TJK", "Tajikistan"
            "TZA", "Tanzania United Republic of"
            "THA", "Thailand"
            "TLS", "Timor-Leste"
            "TGO", "Togo"
            "TON", "Tonga"
            "TTO", "Trinidad and Tobago"
            "TUN", "Tunisia"
            "TCA", "Turk and Caicos Islands"
            "TUR", "Turkey"
            "TUV", "Tuvalu"
            "UGA", "Uganda"
            "UKR", "Ukraine"
            "ARE", "United Arab Emirates"
            "USA", "United States"
            "URY", "Uruguay"
            "VEN", "Venezuela"
            "VNM", "Viet Nam"
            "YEM", "Yemen"
            "ZMB", "Zambia"
            "ZWE", "Zimbabwe"
        ]

    let disabilities =
        [
            "", "Not entered"
            "ZZ", "Not stated"
            "4", "Learning Disability"
            "1", "Behaviour and emotional"
            "2", "Hearing"
            "3", "Manual dexterity"
            "5", "Mobility and gross motor"
            "6", "Perception of physical danger"
            "7", "Personal, self-care and continence"
            "8", "Progressive conditions and physical health"
            "9", "Sight"
            "10", "Speech"
            "NN", "No disability"
            "XX", "Other"
        ]

    let parentalStatuses =
        [
            "", "Not entered"
            "11", "I have children - they all live with me"
            "12", "I have children - some of them live with me"
            "13", "I have children but they don't live with me"
            "14", "I don't have children"
            "15", "I don't want to answer"
        ]

    let employmentStatuses =
        [
            "", "Not entered"
            "1", "I am in regular employment"
            "2", "I am a student"
            "9", "I am long term sick or disabled"
            "10", "Homemaker"
            "12", "I am unemployed but I am seeking work"
            "13", "I am unemployed but not receiving benefits"
            "14", "I do unpaid voluntary work"
            "15", "I have retired"
            "16", "I am unemployed and I'm not seeking work"
            "99", "I don't want to say"
            "5", "Something not mentioned here"
        ]

    type SelfReferralForm =
        {
            Id: System.Guid
            Date: DateTime
            Postcode: string
            UsesAlcohol: bool;
            UsesAmphetamines: bool;
            UsesCocaine: bool;
            UsesEcstasy: bool;
            UsesKetamine: bool;
            UsesCannabis: bool;
            UsesCrackCocaine: bool;
            UsesPrescribedOrOverTheCounter: bool;
            UsesSpice: bool;
            UsesOtherDrug: bool;
            UsesOpiates: bool;
            Forename: string
            Surname: string
            DateOfBirth: DateTime
            Gender: Gender
            EmailAddress: string
            Address: string
            TelephoneNumber: string
            OkayToLeaveAMessage: bool
            Sexuality: string option
            Ethnicity: string option
            Nationality: string option
            Disability: string option
            Parental: int option
            Employment: int option
            PsychologicalHealth: int
            PhysicalHealth: int
            OverallHealth: int
            AgeFirstUsedAlcohol: int option
            HowOftenDrink: int option
            DrinkingDayUnits: int option
            AlcoholBingeFrequency: int option
            Smoking: string
            SubstanceUseDescription: string
            ConcernedAboutUsage: string
            PersonalHabitsAndChanges: string
        }

    let zero date = 
        { 
            Id = Guid.Empty
            Date = date
            Postcode = "" 
            UsesAlcohol = false
            UsesAmphetamines = false
            UsesCocaine = false
            UsesEcstasy = false
            UsesKetamine = false
            UsesCannabis = false
            UsesCrackCocaine = false
            UsesPrescribedOrOverTheCounter = false
            UsesSpice = false
            UsesOtherDrug = false
            UsesOpiates = false
            Forename = ""
            Surname = ""
            DateOfBirth = DateTime.MinValue
            Gender = Male
            EmailAddress = ""
            Address = ""
            TelephoneNumber = ""
            OkayToLeaveAMessage = false
            Sexuality = None
            Ethnicity = None
            Nationality = None
            Disability = None
            Parental = None
            Employment = None
            PsychologicalHealth = 0
            PhysicalHealth = 0
            OverallHealth = 0
            AgeFirstUsedAlcohol = None
            AlcoholBingeFrequency = None
            Smoking = ""
            SubstanceUseDescription = ""
            ConcernedAboutUsage = ""
            PersonalHabitsAndChanges = ""
            HowOftenDrink = None
            DrinkingDayUnits = None
        }

    type ReferralFormSet =
        {
            SelfReferral: SelfReferralForm
            AlcoholAudit: AlcoholAuditAnswers option
        }
    
    let usesAny (form: SelfReferralForm) =
        form.UsesAlcohol || form.UsesAmphetamines || form.UsesCocaine || form.UsesEcstasy || form.UsesKetamine || form.UsesCannabis || form.UsesCrackCocaine || form.UsesPrescribedOrOverTheCounter || form.UsesSpice || form.UsesOtherDrug || form.UsesOpiates

    open Validation

    let getDOB (referral: SelfReferralForm) = referral.DateOfBirth
    let getEmail (referral: SelfReferralForm) = referral.EmailAddress
    let getPostcode (referral: SelfReferralForm) = referral.Postcode

    let ``date of birth cannot be in the future`` (dob:DateTime) =
        dob.Date > DateTime.Now <?> "Cannot be a future date."

    let ``date of birth cannot be too old`` (dob:DateTime) =
        dob.Date < DateTime(1900, 1, 1) <?> "Cannot be before 1900."

    let dateOfBirthValidations =
        ``date of birth cannot be in the future``
        <*> ``date of birth cannot be too old``

    open SharedValidations

    let allValidations =
        (getEmail >> emailValidations)
        <*> (getPostcode >> postcodeValidations)
        <*> (getDOB >> dateOfBirthValidations)

module Paths =
    let makePath (tenant: TenantRef) (slug: Slug) =
        let (Slug s) = slug
        let (TenantRef t) = tenant
        sprintf "#areas/%s/pages/%s" t s

module PageLogic =
    let blockType =
        function
        | SingleColumn _ -> "Single Column"
        | TwoColumn _ -> "Two Column"
        | MainAndSideBar _ -> "Main Content and Sidebar"
        | CardList _ -> "Card List"

    let parseBlockType id =
        function
        | "Single Column" -> SingleColumn (id,[])
        | "Two Column" -> TwoColumn (id, [], [])
        | "Main Content and Sidebar" -> MainAndSideBar (id, [], [])
        | "Card List" -> CardList (id, Rectangle, [])
        | _ -> failwith "Failed to parse BlockType"

    let formatAccent =
        function
        | Inherited -> "Inherited"
        | Light -> "Light"
        | Dark -> "Dark"
        | Turquoise-> "Turquoise"
        | DarkRed-> "Dark Red"
        | Blue -> "Blue"
        | Grey -> "Grey"

    let parseAccent =
        function
        | "Inherited" -> Inherited
        | "Light" -> Light
        | "Dark" -> Dark
        | "Turquoise" -> Turquoise
        | "Dark Red" -> DarkRed
        | "Blue" -> Blue
        | "Grey" -> Grey
        | _ -> failwith "Could not parse accent"

    let formatContent =
        function
        | Markdown _ -> "Markdown"
        | RawText _ -> "Unformatted text"
        | PageNavigation -> "Automatically generated page navigation"
        | YouTubeEmbed _ -> "Embedded YouTube video"

    let parseFormatContent =
        function 
        | "Markdown" -> Markdown ""
        | "Unformatted text" -> RawText ""
        | "Automatically generated page navigation" -> PageNavigation
        | "Embedded YouTube video" -> YouTubeEmbed ("", "", "", false)
        | _ -> failwith "Could not parse format content"

    let formatJustification =
        function
        | LeftAligned -> "Left Aligned"
        | Justification.Centered -> "Centered"

    let parseJustification =
        function
        | "Left Aligned" -> LeftAligned
        | "Centered" -> Justification.Centered
        | _ -> failwith "Could not parse justification"

    let getThemeMatcher targetTheme = 
        match targetTheme with
        | IThemeItem.Background _ -> fun x -> match x with Background b -> Some b | _ -> None
        | IThemeItem.Colour _ -> fun x -> match x with  Colour b -> Some b | _ -> None
        | IThemeItem.Font _ -> fun x -> match x with  _ -> None
        | IThemeItem.FontSize _ -> fun x -> match x with  FontSize b -> Some b | _ -> None

    let getFromTheme targetTheme pageTheme =
        let matcher = getThemeMatcher targetTheme
        match pageTheme |> List.tryPick matcher with
        | None -> ""
        | Some x -> x

module Elements =
    let swapId idGetter direction id list =
        let rec loop blocks prev acc finished =
            match blocks, prev with
            | [], _ -> acc |> List.rev
            | H::T, _ when finished -> loop T None (H::acc) true
            | H::T, (Some p) -> 
                if idGetter H = id then
                    match direction with
                    | Down -> loop T (Some H) (prev.Value::acc) false // should succeed next iteration
                    | Up -> loop T None (prev.Value::H::acc) true
                else if idGetter p = id then
                    match direction with
                    | Up -> loop T None (H::prev.Value::acc) true
                    | Down -> loop T None (prev.Value::H::acc) true
                else
                    loop T (Some H) (prev.Value::acc) false
            | H::T,_ -> loop T (Some H) acc false
        loop list None [] false
    let swapById idGetter direction item list =
        swapId idGetter direction (idGetter item) list